import React, { useEffect, useState } from 'react';
import { useSeasonState } from './store';

/**
 * Cuenta regresiva a Navidad, al centro de la pantalla, al hacer clic en el regalo.
 * Se cierra con la X, con Escape o haciendo clic fuera de la tarjeta. Los dígitos
 * que cambian se animan (encogen, suben y vuelven), como en el original de
 * iCodeThis; el resto de la interfaz queda debajo, oscurecida.
 */
type Unit = 'months' | 'days' | 'hours' | 'minutes' | 'seconds';

const UNITS: { id: Unit; label: string; delay: number }[] = [
  { id: 'months', label: 'Meses', delay: 1800 },
  { id: 'days', label: 'Días', delay: 1500 },
  { id: 'hours', label: 'Horas', delay: 1200 },
  { id: 'minutes', label: 'Minutos', delay: 800 },
  { id: 'seconds', label: 'Segundos', delay: 500 },
];

/**
 * La próxima Navidad: el 25 de diciembre a las 00:00 local de este año si `now` es
 * anterior o igual a ese instante, si no la del año siguiente.
 */
export const nextChristmas = (now: Date): Date => {
  const thisYear = new Date(now.getFullYear(), 11, 25, 0, 0, 0);
  return now <= thisYear ? thisYear : new Date(now.getFullYear() + 1, 11, 25, 0, 0, 0);
};

const remaining = (target: Date, now: Date): Record<Unit, number> => {
  const diff = Math.max(target.getTime() - now.getTime(), 0);
  const DAY = 1000 * 60 * 60 * 24;
  const MONTH = DAY * 30.44;
  const YEAR = DAY * 365.25;
  return {
    months: Math.floor((diff % YEAR) / MONTH),
    days: Math.floor((diff % MONTH) / DAY),
    hours: Math.floor((diff % DAY) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
};

const pad2 = (n: number) => n.toString().padStart(2, '0');

const CountdownOverlay: React.FC = () => {
  const { effect, effectSeq } = useSeasonState();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  // El objetivo se fija EN CADA APERTURA, no al montar (E07-F01, R8 / P9): el componente
  // se monta el 4-nov con la temporada y una pestaña abierta del 24 al 26 de diciembre
  // abriría el 26 una cuenta en ceros hacia una Navidad ya pasada.
  const [target, setTarget] = useState(() => nextChristmas(new Date()));

  useEffect(() => {
    if (effect === 'countdown' && effectSeq > 0) {
      const at = new Date();
      setTarget(nextChristmas(at));
      setNow(at);
      setOpen(true);
    }
  }, [effect, effectSeq]);

  useEffect(() => {
    if (!open) return undefined;
    const tick = window.setInterval(() => setNow(new Date()), 1000);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearInterval(tick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!open) return null;
  const values = remaining(target, now);

  return (
    <div className="countdown-overlay" role="dialog" aria-label="Cuenta regresiva a Navidad" onClick={() => setOpen(false)}>
      <div className="countdown-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="countdown-close" aria-label="Cerrar" onClick={() => setOpen(false)}>
          ×
        </button>
        <h1>Días para Navidad</h1>
        <div className="countdown">
          {UNITS.map((u) => (
            <div key={u.id} data-desc={u.label} style={{ ['--d' as string]: `${u.delay}ms` }}>
              {pad2(values[u.id])
                .split('')
                .map((digit, i) => (
                  // La clave incluye el dígito: al cambiar, el span se monta de nuevo y
                  // corre la animación de entrada (encoge, sube, vuelve).
                  <span key={`${i}-${digit}`}>{digit}</span>
                ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CountdownOverlay;
